import {
  DealType,
  FlowScenario,
  FlowScenarios,
  OrderScenario,
} from '@ca/common/types'
import useCartState from './useCartState'
import useSession from 'hooks/useSession'
import { useActiveDeal } from './useDeal'
import useFlowName from './useFlowName'
import { useMemo } from 'react'
import { OrderState, SavedOrder } from './state/orderState'
import { OrderItem } from '@order/orderTypes'

export const getScenario = (
  orderType: 'base' | 'extra',
  dealType: DealType,
  flowScenario?: string
): OrderScenario => {
  if (flowScenario && isFlowScenario(flowScenario)) {
    return `${orderType}.${flowScenario}` satisfies OrderScenario
  }
  return `${orderType}.${dealType}` satisfies OrderScenario
}

export const useCurrentScenario = (): OrderScenario => {
  const activeDeal = useActiveDeal()
  const pendingOrder = useSession((s) => s.pendingOrder)
  const flowName = useFlowName()

  const dealType = activeDeal?.type || 'normal'
  const orderType = !pendingOrder?.savedOrderId ? 'base' : 'extra'

  return getScenario(orderType, dealType, flowName)
}

const isFlowScenario = (scenario: string): scenario is FlowScenario => {
  return FlowScenarios.includes(scenario as FlowScenario)
}

type OrderScenarios = [OrderScenario, ...OrderScenario[]]
/**
 * This only returns the scenario based on the first item
 */
export const useOrderScenarios = (orderId: string): OrderScenarios => {
  const order = useCartState((s) => s.orders.find((o) => o.id === orderId))
  const orderType = order?.primaryId ? 'extra' : 'base'
  const deal = order?.deals.base?.dealType || 'normal'

  const items = order?.order?.items

  return useMemo(() => {
    if (!items || items.length === 0) {
      // Return the default scenario for the order
      return [getScenario(orderType, deal)]
    }

    return items.map((item) => getScenario(orderType, deal, item?.flowName))
  }, [items, deal, orderType]) as OrderScenarios
}

const resolveBundle = (order: SavedOrder, item: OrderItem) => {
  if (item.bundleType !== 'base') {
    return item.bundleType
  }
  return order.primaryId ? 'extra' : 'base'
}

export const useOrdersScenarios = (orderIds: string[]): OrderScenario[] => {
  const allOrders = useCartState((s) => s.orders)

  return useMemo(() => {
    const orders = allOrders.filter((o) =>
      o.order.items.some((item) => orderIds.includes(item.id))
    )
    const scenarios = orders.flatMap((o) => {
      return o.order.items.map((item) => {
        const bundle = resolveBundle(o, item)
        return getScenario(
          bundle,
          o.deals[bundle]?.dealType || 'normal',
          item.flowName
        )
      })
    })
    // Remove duplicates
    return [...new Set(scenarios)]
  }, [allOrders, orderIds])
}
